// CollapsibleBoundary — thin vertical fold button used on the right edge of
// a sidebar/panel (#1458). Click or Enter/Space toggles `collapsed`; parent
// is responsible for applying the width change. Chevron points in the
// direction you'll move when toggled.
//
// Kept intentionally small and self-contained — no V palette import, colors
// pass through via props with sensible defaults so this is reusable.
import { useState } from 'react';

const HIT_WIDTH = 8;    // thin hit target
const CHEVRON_HEIGHT = 32;

export default function CollapsibleBoundary({
  collapsed,
  onToggle,
  side = 'right',       // which side of the boundary the panel sits on
  ariaLabel = 'Toggle panel',
  borderColor = '#d8d8dc',
  iconColor = '#6e6e73',
  hoverBg = '#eeeef0',
}) {
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);

  // Chevron direction: when the panel is on the LEFT of this boundary and
  // is expanded, clicking collapses leftward — chevron points LEFT ('‹').
  // When collapsed, clicking expands rightward — chevron points RIGHT ('›').
  // Mirror when panel is on the right side.
  const chev = side === 'right'
    ? (collapsed ? '›' : '‹')
    : (collapsed ? '‹' : '›');

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle?.();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-expanded={!collapsed}
      onClick={onToggle}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={{
        width: HIT_WIDTH,
        flexShrink: 0,
        background: (hover || focus) ? hoverBg : 'transparent',
        borderLeft: side === 'right' ? `1px solid ${borderColor}` : 'none',
        borderRight: side === 'left' ? `1px solid ${borderColor}` : 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        outline: focus ? `2px solid ${iconColor}` : 'none',
        outlineOffset: -2,
        transition: 'background 0.15s ease',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          height: CHEVRON_HEIGHT,
          lineHeight: `${CHEVRON_HEIGHT}px`,
          fontSize: 14,
          color: iconColor,
          opacity: (hover || focus) ? 1 : 0.45,
          transition: 'opacity 0.15s ease',
          userSelect: 'none',
        }}
      >
        {chev}
      </span>
    </div>
  );
}
